import { PayloadAction } from '@reduxjs/toolkit';
import { message } from 'antd';
import { AxiosResponse } from 'axios';
import { FieldPlannerTab } from 'features/Yucollect/AgencyMonitor/types/AllocationListDataType';
import { Column } from 'features/Yucollect/utils/types';
import { put, takeLatest } from 'redux-saga/effects';
import { fetchAutoAssignStatus } from 'redux/slice/allocation/AllocationDetails.slice';
import {
  autoAssignAgent,
  autoAssignAgentFailure,
  autoAssignAgentSuccess,
  getFieldAgents,
  getFieldAgentsFailure,
  getFieldAgentsSuccess,
  manualAssignAgent,
  manualAssignAgentFailure,
  manualAssignAgentSuccess,
  reAssignAgent,
  reAssignAgentFailure,
  reAssignAgentSuccess,
  scheduleVisit,
  scheduleVisitFailure,
  scheduleVisitSuccess,
  setActiveFieldPlannerTab,
  unAssignAgent,
  unAssignAgentFailure,
  unAssignAgentSuccess,
} from 'redux/slice/field/FieldPlanner.slice';
import {
  IGetFieldAgentsRequest,
  IGetFieldAgentsResponse,
  IScheduleVisitRequest,
  IUnassignFieldAgentRequest,
} from 'redux/types/Call.types';
import { fieldPlannerEndPoints } from 'services/api/endPoints';
import { HTTPClient } from 'services/api/http-client';
import { getSafeErrorMessage } from 'utils/errorUtils';

// Saga for auto assigning agents
function* autoAssignAgentsSaga(
  action: PayloadAction<{ allocationId: string }>
) {
  try {
    const { allocationId } = action.payload;
    const response: AxiosResponse<{ message: string }> =
      yield HTTPClient.PostRequest(
        `${fieldPlannerEndPoints.AUTO_ASSIGN_AGENTS(allocationId)}`
      );
    yield put(autoAssignAgentSuccess(response.data));
    yield put(setActiveFieldPlannerTab(FieldPlannerTab.ALLOCATED_ACCOUNTS));
    // Call fetch auto assignment
    const fetchAutoAssignStatusAction = {
      type: fetchAutoAssignStatus.type,
      payload: {
        allocationId,
      },
    };
    yield put(fetchAutoAssignStatusAction); // Dispatch the action
  } catch (error: any) {
    yield put(autoAssignAgentFailure(error?.message));
    message.error(getSafeErrorMessage(error));
  }
}

// Saga for manual field agent assignment
function* manualAssignAgentSaga(
  action: PayloadAction<{
    columns: Column[];
    supervisorId: string;
    selectedBorrowerIds: string[];
    unselectedBorrowerIds: string[];
    allocationId: string;
    agentId: string;
    applyToAllAccounts: boolean;
    ungrouped: boolean;
    isAllocated: boolean;
  }>
) {
  try {
    const {
      columns,
      supervisorId,
      selectedBorrowerIds,
      unselectedBorrowerIds,
      allocationId,
      agentId,
      applyToAllAccounts,
      ungrouped = false,
      isAllocated = false,
    } = action.payload;
    const response: AxiosResponse<{ message: string }> =
      yield HTTPClient.PostRequest(
        `${fieldPlannerEndPoints.MANUAL_FIELD_AGENT_ASSIGN(
          ungrouped,
          isAllocated
        )}`,
        {
          columns,
          supervisor_id: supervisorId,
          selectedBorrowerIds,
          applyToAllAccounts,
          unselectedBorrowerIds,
          allocation_id: allocationId,
          agent_id: agentId,
        }
      );
    yield put(manualAssignAgentSuccess(response.data));
  } catch (error: any) {
    yield put(manualAssignAgentFailure(error?.message));
    message.error(getSafeErrorMessage(error));
  }
}
// Saga for reassign field agent
function* reAssignAgentSaga(
  action: PayloadAction<{
    columns: Column[];
    supervisorId: string;
    selectedBorrowerIds: string[];
    unselectedBorrowerIds: string[];
    allocationId: string;
    agentId: string;
    applyToAllAccounts: boolean;
    ungrouped: boolean;
    isAllocated: boolean;
  }>
) {
  try {
    const {
      columns,
      supervisorId,
      selectedBorrowerIds,
      unselectedBorrowerIds,
      allocationId,
      agentId,
      applyToAllAccounts,
      ungrouped = false,
      isAllocated = false,
    } = action.payload;
    const response: AxiosResponse<{ message: string }> =
      yield HTTPClient.PostRequest(
        `${fieldPlannerEndPoints.REASSIGN_FIELD_AGENT_ASSIGN(
          ungrouped,
          isAllocated
        )}`,
        {
          columns,
          supervisor_id: supervisorId,
          selectedBorrowerIds,
          applyToAllAccounts,
          unselectedBorrowerIds,
          allocation_id: allocationId,
          agent_id: agentId,
        }
      );
    yield put(reAssignAgentSuccess(response.data));
  } catch (error: any) {
    yield put(reAssignAgentFailure(error?.message));
    message.error(getSafeErrorMessage(error));
  }
}
// Saga for unassigning agent
function* unAssignAgentSaga(action: PayloadAction<IUnassignFieldAgentRequest>) {
  try {
    const {
      allocationId,
      applyToAllAccounts,
      selectedBorrowerIds,
      columns,
      unselectedBorrowerIds,
      supervisorId,
      ungrouped = false,
      isAllocated = false,
    } = action.payload;
    const response: AxiosResponse<{ message: string }> =
      yield HTTPClient.PostRequest(
        `${fieldPlannerEndPoints.UNASSIGN_FIELD_AGENT(ungrouped, isAllocated)}`,
        {
          supervisor_id: supervisorId,
          selectedBorrowerIds,
          unselectedBorrowerIds,
          columns,
          allocation_id: allocationId,
          applyToAllAccounts,
        }
      );
    yield put(unAssignAgentSuccess(response.data));
  } catch (error: any) {
    yield put(unAssignAgentFailure(error?.message));
    message.error(getSafeErrorMessage(error));
  }
}
// Saga for getting agents by agency and channel
function* getFieldAgentsSaga(action: PayloadAction<IGetFieldAgentsRequest>) {
  try {
    const { supervisorId, channel } = action.payload;
    const response: AxiosResponse<IGetFieldAgentsResponse[]> =
      yield HTTPClient.GetRequest(
        `${fieldPlannerEndPoints.GET_AGENTS(channel, supervisorId)}`
      );
    yield put(getFieldAgentsSuccess(response.data));
  } catch (error: any) {
    yield put(getFieldAgentsFailure(error?.message));
  }
}

// Saga for scheduling visit
function* scheduleVisitSaga(action: PayloadAction<IScheduleVisitRequest>) {
  try {
    const {
      scheduledStartDate,
      scheduledEndDate,
      allocationFileId,
      columns,
      selectedBorrowerIds,
      unselectedBorrowerIds,
      ungrouped = false,
      isAllocated = false,
    } = action.payload;
    const response: AxiosResponse = yield HTTPClient.PostRequest(
      `${fieldPlannerEndPoints.SCHEDULE_VISIT(ungrouped, isAllocated)}`,
      {
        scheduledStartDate,
        scheduledEndDate,
        allocationFileId,
        columns,
        selectedBorrowerIds,
        unselectedBorrowerIds,
      }
    );
    yield put(scheduleVisitSuccess(response.data));
  } catch (error: any) {
    yield put(scheduleVisitFailure(error?.message));
    message.error(getSafeErrorMessage(error));
  }
}

export function* watchFieldPlannerSagas() {
  yield takeLatest(autoAssignAgent.type, autoAssignAgentsSaga);
  yield takeLatest(manualAssignAgent.type, manualAssignAgentSaga);
  yield takeLatest(reAssignAgent.type, reAssignAgentSaga);
  yield takeLatest(unAssignAgent.type, unAssignAgentSaga);
  yield takeLatest(getFieldAgents.type, getFieldAgentsSaga);
  yield takeLatest(scheduleVisit.type, scheduleVisitSaga);
}
